import api from '../../lib/api';

const PIE_PERSONA_KEY = 'pie.persona_id';
const PIE_SESSION_KEY = 'pie.coach_session_id';
const PIE_COACH_PRESEED_KEY = 'pie.coach_preseed';

export const piePersona = {
  get: () => sessionStorage.getItem(PIE_PERSONA_KEY) || null,
  set: (id) => sessionStorage.setItem(PIE_PERSONA_KEY, id),
  clear: () => sessionStorage.removeItem(PIE_PERSONA_KEY),
};

export const pieCoachSession = {
  get: () => sessionStorage.getItem(PIE_SESSION_KEY) || null,
  set: (id) => sessionStorage.setItem(PIE_SESSION_KEY, id),
  clear: () => sessionStorage.removeItem(PIE_SESSION_KEY),
};

/**
 * pieCoachPreseed — used by the "Talk it over" entry points (Improve,
 * Simulator, Validate) to hand the Coach a context-aware opening
 * message. The Coach page reads it once on mount, auto-sends it as if
 * the user typed it, then clears the key.
 */
export const pieCoachPreseed = {
  get: () => sessionStorage.getItem(PIE_COACH_PRESEED_KEY) || null,
  set: (text) => sessionStorage.setItem(PIE_COACH_PRESEED_KEY, text),
  clear: () => sessionStorage.removeItem(PIE_COACH_PRESEED_KEY),
};

function withPersonaParam(params = {}) {
  const id = piePersona.get();
  return id ? { ...params, persona_id: id } : params;
}

export const pieApi = {
  listPersonas: () => api.get('/personal/personas').then((r) => r.data),
  activePersona: () => api.get('/personal/active-persona', { params: withPersonaParam() }).then((r) => r.data),
  dataSources: () => api.get('/personal/data-sources', { params: withPersonaParam() }).then((r) => r.data),
  snapshot: () => api.get('/personal/snapshot', { params: withPersonaParam() }).then((r) => r.data),
  personalLei: () => api.get('/personal/lei', { params: withPersonaParam() }).then((r) => r.data),
  listActions: () => api.get('/personal/actions').then((r) => r.data),
  simulate: (action_id) =>
    api.post('/personal/simulate', { action_id, persona_id: piePersona.get() }).then((r) => r.data),
  coach: (message, session_id) =>
    api
      .post('/personal/coach', {
        message,
        session_id,
        persona_id: piePersona.get(),
      })
      .then((r) => r.data),
  coachHistory: (session_id) =>
    api.get('/personal/coach/history', { params: { session_id } }).then((r) => r.data),
  // ── Commitments / "Did it work?" ─────────────────────
  createCommitment: (action_id) =>
    api.post('/personal/commitments', {
      action_id,
      persona_id: piePersona.get(),
      // When the user is mid-coaching, link the commitment back to the
      // conversation that produced it so /me/validate can offer a
      // "↗ where this came from" deep-link.
      coach_session_id: pieCoachSession.get() || undefined,
    }).then((r) => r.data),
  listCommitments: () =>
    api.get('/personal/commitments', { params: withPersonaParam() }).then((r) => r.data),
  validateCommitment: (commitment_id, payload) =>
    api.post(`/personal/commitments/${commitment_id}/validate`, payload).then((r) => r.data),
  // ── AI Insight Engine ────────────────────────────────
  insights: () =>
    api.get('/personal/insights', { params: withPersonaParam() }).then((r) => r.data),
  // ── Weekly Plan (Coach → Dashboard habit loop) ──────
  saveWeeklyPlan: (planText, sourceMessageId, sourceCommitmentId) =>
    api
      .post('/personal/weekly-plan/save', {
        plan_text: planText,
        source_message_id: sourceMessageId || null,
        source_commitment_id: sourceCommitmentId || null,
        persona_id: piePersona.get(),
      })
      .then((r) => r.data),
  activeWeeklyPlan: () =>
    api.get('/personal/weekly-plan/active', { params: withPersonaParam() }).then((r) => r.data),
  toggleWeeklyPlanDay: (planId, dayNum, status) =>
    api
      .patch(`/personal/weekly-plan/${planId}/days/${dayNum}`, { status })
      .then((r) => r.data),
  archiveWeeklyPlan: (planId) =>
    api.post(`/personal/weekly-plan/${planId}/archive`).then((r) => r.data),
  retrospectWeeklyPlan: (planId, payload) =>
    api.post(`/personal/weekly-plan/${planId}/retrospect`, payload).then((r) => r.data),
  weeklyPlanHistory: () =>
    api.get('/personal/weekly-plan/history', { params: withPersonaParam() }).then((r) => r.data),
  weeklyPlanStreak: () =>
    api.get('/personal/weekly-plan/streak', { params: withPersonaParam() }).then((r) => r.data),
  // ── Coach welcome greeting (used by the empty-state) ──────
  coachWelcome: () =>
    api.get('/personal/coach/welcome', { params: withPersonaParam() }).then((r) => r.data),
  coachSessionVerdict: (session_id) =>
    api.get('/personal/coach/session-verdict', { params: { session_id } }).then((r) => r.data),
  // ── Reflection summary (hands-free wrap-up card) ─────
  generateReflectionSummary: (session_id, turns_window = 6) =>
    api
      .post('/personal/coach/reflection-summary', { session_id, turns_window })
      .then((r) => r.data),
  saveReflectionSummary: (summary_id) =>
    api.post(`/personal/coach/reflection-summary/${summary_id}/save`).then((r) => r.data),
  listReflectionSummaries: (onlySaved = false, limit = 20) =>
    api
      .get('/personal/coach/reflection-summary/list', { params: { only_saved: onlySaved, limit } })
      .then((r) => r.data),
  // ── Month-in-Review (growth artifact) ─────────────────
  monthInReviewCurrent: () =>
    api.get('/personal/coach/month-in-review/current').then((r) => r.data),
  generateMonthInReview: () =>
    api.post('/personal/coach/month-in-review/generate', {}).then((r) => r.data),
  saveMonthInReview: (review_id) =>
    api.post(`/personal/coach/month-in-review/${review_id}/save`).then((r) => r.data),
  listMonthInReviews: (onlySaved = true, limit = 12) =>
    api
      .get('/personal/coach/month-in-review/list', { params: { only_saved: onlySaved, limit } })
      .then((r) => r.data),
  // ── Manager "My team" view ──────────────────────────
  teamSnapshot: () =>
    api.get('/personal/team/snapshot', { params: withPersonaParam() }).then((r) => r.data),
  teamSuggestions: () =>
    api.get('/personal/team/suggestions', { params: withPersonaParam() }).then((r) => r.data),
  // ── "What changed since last visit" strip ───────────
  whatsChanged: () =>
    api.get('/personal/whats-changed', { params: withPersonaParam() }).then((r) => r.data),
  whatsChangedAck: (current) =>
    api.post('/personal/whats-changed/ack', { current }, { params: withPersonaParam() }).then((r) => r.data),
  // ── Weekly "Coach's recap" digest ───────────────────
  digestPreview: () =>
    api.get('/personal/digest/preview', { params: withPersonaParam() }).then((r) => r.data),
  digestSend: () =>
    api.post('/personal/digest/send', { persona_id: piePersona.get() }).then((r) => r.data),
  // ── Public health probe for the in-app status pill ────
  health: () =>
    api.get('/personal/health').then((r) => r.data),
  // ── Weekly learner pulse ──────────────────────────────
  pulseCurrent: () =>
    api.get('/personal/pulse/current', { params: withPersonaParam() }).then((r) => r.data),
  pulseSubmit: (payload) =>
    api.post(
      '/personal/pulse/submit',
      { ...payload, persona_id: piePersona.get() },
    ).then((r) => r.data),
  pulseHistory: (weeks = 12) =>
    api.get('/personal/pulse/history', { params: { ...withPersonaParam(), weeks } }).then((r) => r.data),
  // ── Manager weekly pulse (batched roster rating) ──────
  managerPulseCurrent: () =>
    api.get('/personal/manager/pulse/current', { params: withPersonaParam() }).then((r) => r.data),
  managerPulseSubmit: (ratings) =>
    api.post('/personal/manager/pulse/submit', {
      persona_id: piePersona.get(),
      ratings,
    }).then((r) => r.data),
  managerPulseHistory: (weeks = 8) =>
    api.get('/personal/manager/pulse/history', { params: { ...withPersonaParam(), weeks } }).then((r) => r.data),
  // ── M3: Trends + Heatmap ─────────────────────────────
  trendsLearner: (weeks = 12) =>
    api.get('/personal/trends/learner', { params: { ...withPersonaParam(), weeks } }).then((r) => r.data),
  trendsTeamHeatmap: () =>
    api.get('/personal/trends/team-heatmap', { params: withPersonaParam() }).then((r) => r.data),
  // ── M4: Quarterly Motivation Survey + Calibration ──
  motivationSurveyCurrent: () =>
    api.get('/personal/motivation-survey/current', { params: withPersonaParam() }).then((r) => r.data),
  motivationSurveySubmit: (payload) =>
    api.post('/personal/motivation-survey/submit', { ...payload, persona_id: piePersona.get() }).then((r) => r.data),
  calibrationMe: (quarters = 4) =>
    api.get('/personal/calibration/me', { params: { ...withPersonaParam(), quarters } }).then((r) => r.data),
  calibrationReview: () =>
    api.get('/personal/calibration/review').then((r) => r.data),
};
